/**
 * One-shot: restack the saved "Options" workspace onto the Trade + Chain
 * desk. The previous seed was nine TOS panes (strategy, chain, strikes,
 * stats, payoff, risk, positions, tape, flow) which made the working trade
 * impossible to follow. Trees that already contain `opt:trade` are left
 * alone — a user who rebuilt after this cutover stays rebuilt.
 *
 * Flagged by `mpp:migrate:options-trade-desk:v1`.
 */
import { safeLocalStorageGet, safeLocalStorageSet } from "@/lib/safeLocalStorage";
import type { WsNode, WorkspacePage } from "@/lib/workspace/workspacePages";

function nid(prefix: string): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

const FLAG = "mpp:migrate:options-trade-desk:v1";
const LIST_KEY = "mpp:workspaces:v1";
const PAGE_NAME = "options";
const TRADE = "opt:trade";

function treeHas(node: WsNode, id: string): boolean {
  if (node.t === "leaf") return node.component === id;
  return node.children.some((c) => treeHas(c, id));
}

function tradeDesk(): WsNode {
  return {
    id: nid("s"),
    t: "split",
    dir: "h",
    children: [
      { id: nid("l"), t: "leaf", component: TRADE },
      { id: nid("l"), t: "leaf", component: "opt:chain" },
    ],
  };
}

function rewriteTreeJson(raw: string): string | null {
  try {
    const root = JSON.parse(raw) as WsNode;
    if (!root || (root.t !== "leaf" && root.t !== "split")) return null;
    if (treeHas(root, TRADE)) return null;
    return JSON.stringify(tradeDesk());
  } catch {
    return null;
  }
}

/** Run once per browser profile. Safe to call on every app boot.
 *  Does NOT call loadPages/savePages — those call back into this migrator. */
export function migrateOptionsTradeDesk(): void {
  if (typeof window === "undefined") return;
  if (safeLocalStorageGet(FLAG) === "1") return;

  let pool: WorkspacePage[] = [];
  try {
    const raw = safeLocalStorageGet(LIST_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as WorkspacePage[];
      if (Array.isArray(arr)) pool = arr;
    }
  } catch {
    pool = [];
  }

  const optionsIds = new Set<string>();
  let poolChanged = false;
  const nextPool: WorkspacePage[] = pool.map((p) => {
    if (!p?.root) return p;
    const isOptions = typeof p.name === "string" && p.name.trim().toLowerCase() === PAGE_NAME;
    if (!isOptions) return p;
    optionsIds.add(p.id);
    if (treeHas(p.root, TRADE)) return p;
    poolChanged = true;
    return { ...p, root: tradeDesk(), updatedAt: Date.now() };
  });
  if (poolChanged) safeLocalStorageSet(LIST_KEY, JSON.stringify(nextPool));

  try {
    const writes: Array<{ key: string; value: string }> = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.includes("mpp:ws-tree:")) continue;
      const matches = [...optionsIds].some((id) => key.includes(id));
      if (!matches) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const next = rewriteTreeJson(raw);
      if (next) writes.push({ key, value: next });
    }
    for (const { key, value } of writes) {
      safeLocalStorageSet(key.includes("\u0000@dp:") ? key.split("\u0000@dp:")[0]! : key, value);
      if (key.includes("\u0000@dp:")) {
        try {
          window.localStorage.setItem(key, value);
        } catch {
          /* quota — logical write above is the one that matters */
        }
      }
    }
  } catch {
    /* pool migration still stands */
  }

  safeLocalStorageSet(FLAG, "1");
}
